import { useEffect, useState } from 'react';
import {
  Admin,
  Users,
  type AreaDeployConfigRow,
  type TicketArea,
  type UserSummary,
} from '../../lib/api';

const AREAS: { value: TicketArea; label: string }[] = [
  { value: 'CUSTOMER_WEBSITE', label: 'Customer website' },
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'OTHER', label: 'Other' },
];

export function DeployReviewersAdminPage() {
  const [config, setConfig] = useState<AreaDeployConfigRow[]>([]);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<TicketArea | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [c, u] = await Promise.all([Admin.getAreaDeployConfig(), Users.list()]);
        setConfig(c.config);
        setUsers(u.users);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function setReviewer(area: TicketArea, reviewerUserId: string | null) {
    setSaving(area);
    setError(null);
    try {
      const res = await Admin.setAreaDeployReviewer(area, reviewerUserId);
      setConfig((prev) => {
        const without = prev.filter((c) => c.area !== area);
        return [...without, res.config];
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(null);
    }
  }

  const byArea = new Map(config.map((c) => [c.area, c]));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Deploy reviewers</h2>
        <p className="text-sm text-slate-500 mt-1">
          When a ticket moves to <strong>Pending Code Review</strong>, the system auto-creates a deploy
          ticket and assigns it to the reviewer for that area. Set the default reviewer per area below.
          Unset means the deploy ticket is created unassigned.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2">Area</th>
              <th className="px-3 py-2">Reviewer</th>
              <th className="px-3 py-2 w-32" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading &&
              AREAS.map(({ value, label }) => {
                const row = byArea.get(value);
                const reviewerId = row?.reviewerUserId ?? '';
                const isSaving = saving === value;
                return (
                  <tr key={value} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{label}</td>
                    <td className="px-3 py-2">
                      <select
                        value={reviewerId}
                        onChange={(e) =>
                          void setReviewer(value, e.target.value === '' ? null : e.target.value)
                        }
                        disabled={isSaving}
                        className="input !w-auto"
                      >
                        <option value="">(unassigned)</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} &lt;{u.email}&gt;
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {isSaving ? 'Saving…' : row?.reviewer ? `Active: ${row.reviewer.name}` : ''}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
